import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GlobalErrorService } from '@services/global-error.service';
import { EmployeeData, UserResponse } from '@interfaces/hiring-manager.model';
import { catchError, from, Observable } from 'rxjs';
import OktaAuth, {
  CustomUserClaims,
  UserClaims,
  Token,
  AccessToken,
} from '@okta/okta-auth-js';
import { Roles, RoutePaths } from '@constants/global.enum';
import { OKTA_AUTH } from '@okta/okta-angular';
import { ToasterService } from '@services/toaster.service';
import { IOPEmails } from './emailList.config';
import { environment } from '@environments/environment';
import { Router } from '@angular/router';
import { DOMAIN_CBRE, employeeLevelCode } from '@constants/constants';
import memoizeObservable from '@app/shared/utils/memoizedObservable';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl = environment.apiUrl;
  private _oktaProfile: UserClaims<CustomUserClaims> | null | undefined;
  private _userProfile: EmployeeData | null | undefined;
  private oktaTokenReAttempts = 0;
  private featureFlag = environment.production;
  private httpClient: HttpClient;

  constructor(
    private readonly http: HttpClient,
    private errorHandler: GlobalErrorService,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private toasterService: ToasterService,
    private router: Router,
    handler: HttpBackend,
  ) {
    this.httpClient = new HttpClient(handler);
  }

  getEmployeeData = memoizeObservable((email: string) => {
    return this.http
      .get<UserResponse>(`${this.baseUrl}/hiring/employee/${email}`)
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  });

  getEmployeeType() {

    if (!this.userProfile) {
      return Roles.UNKNOWN;
    }

    if (!this.featureFlag) {
      if (IOPEmails.includes(this.userProfile.email?.toLocaleLowerCase())) {
        return Roles.IOP;
      }
      // Vendor users are no longer identified by a hard-coded e-mail list: the
      // non-CBRE-domain rule below (and hiring_vendor_details on the backend)
      // is the single source of truth.
    }

    if (this.userProfile.isOpsTeam) {
      return Roles.OPS;
    } else if (
      this.userProfile?.poiType == Roles.EMP &&
      Number(this.userProfile.employeeLevel) <= employeeLevelCode.LEVEL_SIX &&
      Number(this.userProfile.employeeLevel) >= employeeLevelCode.LEVEL_ONE
    ) {
      return Roles.EMP;
    } else if (!this.userProfile?.email.includes(DOMAIN_CBRE)) {
      return Roles.vendor;
    } else if (this.userProfile?.poiType == Roles.CWR) {
      return Roles.CWR;
    } else if (this.userProfile?.poiType == Roles.EMP) {
      // CBRE employee without a leader level — iSkill-only access
      return Roles.EMPLOYEE;
    }
    return Roles.UNKNOWN;
  }

  hasAccessToRoute(url: string): boolean {
    url = url.trim();
    if (!this.userProfile) return false;
    const userRole = this.getEmployeeType();

    if (url.includes(RoutePaths.Finance)) {
      return !!this.userProfile.isFinanceAdmin;
    }

    // Productivity Tracker — scrum masters can upload; leaders / ops / IOP
    // can view the team dashboard. Mirrors the flag combo used by the
    // Productivity menu item's `show` predicate in menu-config.helper.
    if (url.includes(RoutePaths.Productivity)) {
      return !!this.userProfile.isScrumMaster
          || !!this.userProfile.isReporties
          || !!this.userProfile.isOpsTeam
          || userRole === Roles.IOP;
    }

    const empAccess = () =>
      [
        RoutePaths.HireMgr,
        RoutePaths.VendorMgmt,
        RoutePaths.TeamManagement,
        RoutePaths.AssetManagement
      ].some((path) => url.includes(path));

    const opsAccess = () => {
      const urlWithoutQuery = url.split('?')[0];
      const pathSegments = urlWithoutQuery.split('/');
      return [
        RoutePaths.OpsVendorManagement,
        RoutePaths.OpsTeam,
        RoutePaths.HireMgr,
        RoutePaths.VendorMgmt,
        RoutePaths.TeamManagement,
        RoutePaths.AssetManagement,
        RoutePaths.Adoption
      ].some((path) => pathSegments.includes(path));
    };

    // Vendor representatives: Vendor Dashboard plus the view-only iSkill Team Explorer.
    const vendorAccess = () =>
      url.includes(RoutePaths.VendorRep) ||
      (url.includes(RoutePaths.ISkill) && !!this.userProfile?.iskillAccess);

    const cwrAccess = () =>
      url.includes(RoutePaths.VendorEmployee) || url.includes(RoutePaths.ISkill);

    const employeeAccess = () => url.includes(RoutePaths.ISkill);

    const iopAccess = () =>
      [
        RoutePaths.VendorRep,
        RoutePaths.OpsTeam,
        RoutePaths.HireMgr,
        RoutePaths.TeamManagement,
        RoutePaths.AssetManagement,
        RoutePaths.Adoption
      ].some((path) => url.includes(path));

    switch (userRole) {
      case Roles.EMP:
        return empAccess() || url.includes(RoutePaths.ISkill);
      case Roles.OPS:
        return opsAccess() || url.includes(RoutePaths.ISkill);
      case Roles.vendor:
        return vendorAccess();
      case Roles.IOP:
        return true;
      case Roles.CWR:
        return cwrAccess();
      case Roles.EMPLOYEE:
        // A CBRE employee without a leader level but WITH active reportees
        // (data gap: level missing on a manager's record) is routed to
        // /team-management at login — grant them the leader route set so the
        // menu, the landing page and the guards agree.
        return this.userProfile.isReporties
          ? empAccess() || url.includes(RoutePaths.ISkill)
          : employeeAccess();
      default:
        return false;
    }
  }

  set oktaUserProfile(user: UserClaims<CustomUserClaims> | null | undefined) {
    sessionStorage.setItem('oktaUserProfile', JSON.stringify(user));
    this._oktaProfile = user;
  }

  get oktaUserProfile(): UserClaims<CustomUserClaims> | null | undefined {
    if (!this._oktaProfile) {
      this._oktaProfile = JSON.parse(
        sessionStorage.getItem('oktaUserProfile') as string,
      );
    }
    return this._oktaProfile;
  }

  reAttemptOktaToken(): void {
    if (this.oktaTokenReAttempts < 3) {
      this.oktaTokenReAttempts++;
    } else {
      throw new Error('Okta token re-attempts exceeded');
    }
  }

  set userProfile(employeeData: EmployeeData) {
    sessionStorage.setItem('userProfile', JSON.stringify(employeeData));
    this._userProfile = employeeData;
  }

  get userProfile(): EmployeeData | null | undefined {
    if (!this._userProfile) {
      this._userProfile = JSON.parse(
        sessionStorage.getItem('userProfile') as string,
      );
    }
    return this._userProfile;
  }

  get userInitials() {
    if (!this.oktaUserProfile) return '';
    let initials = '';
    if (this.oktaUserProfile?.given_name) {
      initials += this.oktaUserProfile?.given_name[0];
    }
    if (this.oktaUserProfile?.family_name) {
      initials += this.oktaUserProfile?.family_name[0];
    }
    if (initials) return initials.toUpperCase();
    return '?';
  }

  get userGivenName(): string {
  return this.oktaUserProfile?.given_name || '';
  }
  async getEmployeeDetails(): Promise<EmployeeData | null | undefined> {
    if (!this.oktaUserProfile?.email)
      throw new Error('User profile email not found.');
    if (
      this.oktaUserProfile &&
      !!this.oktaUserProfile?.email &&
      this.oktaUserProfile?.email == this.userProfile?.email
    )
      return this._userProfile;
    return new Promise((resolve, reject) => {
      this.getEmployeeData(this.oktaUserProfile?.email as string).subscribe(
        (data) => {
          this.userProfile = data.data;
          resolve(this.userProfile);
        },
      );
    });
  }

  getAccessToken(): Promise<string | null> {
    return this.oktaAuth.tokenManager
      .get('accessToken')
      .then((token: Token | undefined) => {
        if (token && this.isAccessToken(token)) {
          return token.accessToken;
        }
        return null;
      })
      .catch(() => {
        this.signOut();
        return null;
      });
  }

  private isAccessToken(token: Token): token is AccessToken {
    return (token as AccessToken).accessToken !== undefined;
  }

  refreshToken(): Observable<AccessToken> {
    return from(
      this.oktaAuth.tokenManager.renew('accessToken') as Promise<AccessToken>,
    );
  }

  async signOut() {
    sessionStorage.clear();
    try {
      const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
      if (!accessToken || this.oktaAuth.tokenManager.hasExpired(accessToken)) {
        this.router.navigate(['/login']);
        return;
      }
      await this.oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin + '/login',
        clearTokensBeforeRedirect: true,
      });
    } catch (error) {
      this.toasterService.showError(
        'Logout failed, please try again.',
        'Logout Error',
      );
    }
  }
}
